import { Section, SectionProps } from '../../../components/Section/Section';
import {
  Avatar,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Grid,
  Icon,
  Stack,
  Typography,
} from '@mui/material';
import AppLink from '../../../components/Link/AppLink';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import React, { useEffect, useRef, useState } from 'react';
import { HighLightedText } from '../../../components/HighLightedText/HighLightedText';
import { PhoneFrame } from '../../../components/Content/PhoneFrame';
import Image from 'next/image';
import { DemoCTA } from '../../../components/Buttons/DemoCTA';
import { MarqtingPage } from '../../../components/Page/MarqtingPage';
import { MARQTING_CUSTOMER_CASES_ROUTE } from '../../routes';

export default function FunctionaliteitenPage() {
  return (
    <MarqtingPage
      selectedTab={1}
      title="Ontdek de kracht van MarQting | MarQting"
      seo={{
        description:
          'Informatie verzamelen en op een correcte wijze inzetten is de kracht van MarQting. Slimme labels zorgen voor meetbaarheid in de fysieke wereld. Dat geeft de mogelijkheid voor een self-service showroom, een meetbare en traceerbare klantreis en de klant wordt altijd voorzien van de juiste informatie.',
        openGraph: {
          type: 'website',
          locale: 'nl_NL',
          title:
            'Maakt de offline wereld meetbaar. Dat zorgt voor betere beslissingen en meer rendement. | MarQting | AppCenter Nederland',
          description:
            'Informatie verzamelen en op een correcte wijze inzetten is de kracht van MarQting. Slimme labels zorgen voor meetbaarheid in de fysieke wereld. Dat geeft de mogelijkheid voor een self-service showroom, een meetbare en traceerbare klantreis en de klant wordt altijd voorzien van de juiste informatie.',
          images: [
            {
              url: 'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/featured_img_marqting.jpg',
              secureUrl:
                'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/featured_img_marqting.jpg',
              width: 1920,
              height: 1080,
              alt: 'Informatie verzamelen en op een correcte wijze inzetten is de kracht van MarQting. Slimme labels zorgen voor meetbaarheid in de fysieke wereld. Dat geeft de mogelijkheid voor een self-service showroom, een meetbare en traceerbare klantreis en de klant wordt altijd voorzien van de juiste informatie.',
              type: 'image/jpeg',
            },
          ],
          site_name: 'AppCenter Nederland',
        },
      }}
    >
      <Section1 />
      <Section2 />
      <Section3 />
      <Section4 />
    </MarqtingPage>
  );
}

export function Section1() {
  const Usp = ({ icon, label, text }) => (
    <Stack direction="column" spacing={1}>
      <Stack
        direction={'row'}
        sx={{
          justifyContent: 'center',
          color: 'white',
          alignItems: 'center',
        }}
        spacing={2}
      >
        <Avatar
          variant={'rounded'}
          sx={{ bgcolor: 'white', width: 65, height: 65 }}
        >
          <Icon sx={{ fontSize: '2.5rem' }} color={'primary'}>
            {icon}
          </Icon>
        </Avatar>
        <Typography
          fontWeight={600}
          align={'center'}
          color={'primary'}
          sx={{
            background: 'white',
            px: 1,
            py: 2,
            borderRadius: '4px',
            mb: 1,
          }}
          variant={'h5'}
          component={'h3'}
        >
          {label}
        </Typography>
      </Stack>
      <Typography variant={'body1'} align={'center'}>
        {text}
      </Typography>
    </Stack>
  );

  const Usps = ({ children }) => (
    <Stack direction="column" spacing={{ xs: 4, md: 16 }}>
      {children}
    </Stack>
  );

  return (
    <Section
      backgroundColor={'primary.main'}
      position="center"
      caption="BREED TOEPASBAAR IN DIGITALISERING VAN DE WERELD."
      title={
        <>
          Ontdek de{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="kracht"
          />{' '}
          van{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="MarQting"
          />
        </>
      }
      subtitle="Informatie verzamelen en op een correcte wijze inzetten is de kracht van MarQting. Slimme labels zorgen voor meetbaarheid in de fysieke wereld. Dat geeft de mogelijkheid voor een self-service showroom, een meetbare en traceerbare klantreis en de klant wordt altijd voorzien van de juiste informatie."
      actions={[
        <CustomersCTA key={0} />,
        <DemoCTA app={'marqting'} key={1} color="inherit" variant="outlined" />,
      ]}
    >
      <Grid sx={{ mt: 8 }} container spacing={2}>
        <Grid item xs={12} sm={12} md={4} lg={3} xl={3.5}>
          <Usps>
            <Usp
              icon="insights"
              label="Meetbaarheid."
              text={
                <>
                  Maak de fysieke wereld <strong>meetbaar.</strong> krijg
                  inzicht in de resultaten van je{' '}
                  <strong>offline promotie / drukwerk.</strong>
                </>
              }
            />
            <Usp
              icon="alt_route"
              label="Online / offline klantreis."
              text={
                <>
                  Online <strong>gevonden worden</strong>, offline aankopen en
                  andersom. De complete klantreis{' '}
                  <strong>is traceerbaar.</strong>
                </>
              }
            />
          </Usps>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          lg={6}
          xl={5}
          sx={{
            justifyContent: 'center',
            display: 'flex',
            position: 'relative',
          }}
        >
          <Box
            component={'img'}
            sx={{
              mt: { xs: 6, lg: 0 },
              mb: { xs: 6, lg: 0 },
              position: { lg: 'absolute' },
              bottom: -85,
              width: {
                xs: '70%',
                sm: '50%',
                md: '100%',
                lg: '100%',
                xl: '100%',
              },
            }}
            src={
              'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_image_fourth.svg'
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={3} xl={3.5}>
          <Usps>
            <Usp
              icon="new_releases"
              label="Altijd actueel."
              text={
                <>
                  Pas informatie <strong>gemakkelijk achteraf</strong> aan.
                  Informatie is <strong> altijd actueel</strong>, zelfs wanneer
                  het <strong>al gedrukt</strong> is.
                </>
              }
            />
            <Usp
              icon="sensor_occupied"
              label="Laagdrempelig."
              text={
                <>
                  Kom direct <strong>in de telefoon</strong> van jouw
                  (potentiële) klant en blijf daardoor{' '}
                  <strong>top of mind.</strong>
                </>
              }
            />
          </Usps>
        </Grid>
      </Grid>
    </Section>
  );
}

function Section2() {
  return (
    <VideoSection
      position="right"
      videoInnerBoxProps={{ sx: { mt: 1 } }}
      videoOuterBoxProps={{ sx: { height: 575 } }}
      videoSrc="https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/meetbare_campagnes_v2_optimized.mp4"
      caption="SLIM ADVERTEREN IN DRUKWERKCAMPAGNES."
      contentContainerProps={{ sx: { ml: 0, mr: 'auto' } }}
      title={
        <>
          Krijg inzicht in rendement met{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="slimme"
          />{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="drukwerkcampagnes."
          />
        </>
      }
      subtitle="Van advertentie met een oplage van duizenden stuks tot een reclamebord langs de weg. Krijg inzicht in de kosten voor adverteren en de omzet die hieruit voortvloeit. Met een slimme meetbare advertentie van MarQting krijg je inzicht in het rendement en de doelgroep. Maak keuzes voor de juiste advertentiekanalen en de juiste locaties. Hierdoor haal jij meer uit jouw marketing euro's."
      actions={[
        <CustomersCTA key={0} color="primary" />,
        <DemoCTA app={'marqting'} key={1} color="inherit" variant="outlined" />,
      ]}
    />
  );
}

function Section3() {
  return (
    <VideoSection
      backgroundColor="primary.main"
      position="left"
      inversePresentingImage
      videoInnerBoxProps={{ sx: { mt: -0.5 } }}
      videoOuterBoxProps={{ sx: { height: 570, mt: 1 } }}
      contentContainerProps={{ sx: { mr: 0 } }}
      videoSrc="https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/actuele_product_informatie_optimized.mp4"
      caption="ALTIJD ACTUELE PRODUCTINFORMATIE EN PRODUCTPOSITIONERING."
      title={
        <>
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="Interactieve"
          />{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="productinformatie"
          />{' '}
          op het juiste moment.
        </>
      }
      subtitle="Welke tafel is het meest populair? Waar bevinden de mensen in mijn doelgroep zich? Hoe weet ik waar het interessegebied ligt? Er is geen directe data beschikbaar en gedrukt drukwerk ligt nou eenmaal vast. Prijswijzigingen, productinformatie en zelfs positionering van producten waren eerder niet meetbaar. MarQting maakt dit inzichtelijk in transparante overzichtelijke rapportages en informatie is altijd achteraf nog aanpasbaar. "
      actions={[
        <CustomersCTA key={0} />,
        <DemoCTA app={'marqting'} key={1} color="inherit" variant="outlined" />,
      ]}
    />
  );
}

function Section4() {
  return (
    <VideoSection
      position="right"
      videoInnerBoxProps={{ sx: { mt: -0.5 } }}
      videoOuterBoxProps={{ sx: { height: 570, mt: 1 } }}
      contentContainerProps={{ sx: { ml: 0, mr: 'auto' } }}
      videoSrc="https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/visitekaartjes_marqting_optimized.mp4"
      caption="KOM IN DE TELEFOON VAN JE GESPREKSPARTNER."
      title={
        <>
          Val op en blijf top-of-mind met{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="slimme"
          />{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="visitekaartjes."
          />
        </>
      }
      subtitle="Leadgeneratie, persoonlijk contact en contactgegevens uitwisselen hoeft niet meer handmatig. Maak de informatie op je visitekaartje slim. Trigger nieuwsgierigheid van je gesprekspartner en wees ieder jaar, minimaal een dag, top-of-mind bij je lead of bestaande klant. Met MarQting zorgen we ervoor dat alle contactinformatie actueel blijft. Mis nooit meer het juiste contactmoment."
      actions={[
        <CustomersCTA key={0} color="primary" />,
        <DemoCTA app={'marqting'} key={1} color="inherit" variant="outlined" />,
      ]}
    />
  );
}

interface VideoSectionProps extends SectionProps {
  videoSrc: string;
  videoInnerBoxProps?: BoxProps;
  videoOuterBoxProps?: BoxProps;
  inversePresentingImage?: boolean;
}

function VideoSection(props: VideoSectionProps) {
  const {
    videoSrc,
    videoInnerBoxProps,
    videoOuterBoxProps,
    inversePresentingImage,
    ...SectionProps
  } = props;
  const videoRef = useRef<HTMLVideoElement>();
  const [sectionVisible, setSectionVisible] = useState<boolean>(false);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      if (sectionVisible) {
        video.play();
      } else {
        video.pause();
      }
    }
  }, [sectionVisible]);

  return (
    <Section
      onChangeInViewport={(visible) => setSectionVisible(visible)}
      SideComponent={
        <Stack
          direction={'row'}
          spacing={2}
          sx={{
            position: 'relative',
            justifyContent: { sm: 'center', md: 'center', lg: 'unset' },
          }}
          alignItems={{ xs: 'center', md: 'inherit' }}
        >
          <PhoneFrame>
            <Box
              overflow={'hidden'}
              borderRadius={'8px'}
              sx={{ height: 572 }}
              {...videoOuterBoxProps}
            >
              <Box
                ref={videoRef}
                component={'video'}
                muted
                playsInline
                loop
                width={'100%'}
                sx={{ mt: -0.1 }}
                {...videoInnerBoxProps}
              >
                <source src={videoSrc} type="video/mp4" />
              </Box>
            </Box>
          </PhoneFrame>
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: -10, md: -10, lg: -180 },
              right: { xs: -100, sm: 0, md: 50, lg: -24 },
              left: { xs: 'unset', sm: 'unset', md: 'unset', lg: 'unset' },
            }}
          >
            <Image
              src={
                inversePresentingImage
                  ? 'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_image_present_white_shirt_v3.svg'
                  : 'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_image_present_v3.svg'
              }
              height={500}
              width={300}
              alt="TIM"
            />
          </Box>
        </Stack>
      }
      {...SectionProps}
    />
  );
}

const CustomersCTA = (props?: ButtonProps) => (
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  <Button
    fullWidth
    variant="contained"
    size={'large'}
    href={MARQTING_CUSTOMER_CASES_ROUTE}
    component={AppLink}
    color="secondary"
    endIcon={<ArrowForwardIcon />}
    {...props}
  >
    Bekijk klantcases
  </Button>
);
